import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePrefs, useFlags } from 'wsm-common-data';
import logger from 'ws-scripts/modules/logger';

import getDRAdminConfig from '../api/getDRAdminConfig';
import PaymentSelectorView from '../components/PaymentSelectorView';
import StepContainer from '../components/StepContainer';
import { setSteps } from '../features/steps/stepsSlice';
import { setVehicleId } from '../features/vehicleId/vehicleIdSlice';
import { setListingType } from '../features/listingType/listingTypeSlice';
import {
	getAPIVehicleInfo,
	getLsWidgetStateByVehicleId,
	getMmdId,
	updateLsWidgetState
} from '../helpers';

import '../global-sass/main.scss';

/**
 * Fetches DR Admin Config and returns updated steps object.
 * Pure function - easier to test and no stale closures.
 *
 * @param {Object} currentSteps - Current steps state
 * @returns {Promise<{updatedSteps: Object, vehicleId: string}|null>} Updated steps and vehicleId, or null if failed
 */
const fetchAndUpdateDRConfig = async (currentSteps) => {
	const vehicleInfo = await getAPIVehicleInfo();
	const mmdId = getMmdId(vehicleInfo.vehicle, vehicleInfo.dealer);

	if (!mmdId) {
		return null; // No MMD ID found
	}
	const vehicleId = vehicleInfo.vehicle?.uuid;
	if (!vehicleId) {
		return null; // No vehicle ID found
	}

	const drConfig = await getDRAdminConfig(mmdId);

	// Return null if no valid config
	if (!drConfig || drConfig.error) {
		return null;
	}

	// Return new steps object with updated enabled states
	return {
		updatedSteps: {
			payment: {
				...currentSteps.payment,
				enabled: true // Always enabled (core functionality)
			},
			tradeIn: {
				...currentSteps.tradeIn,
				enabled: !!drConfig.tradeInValuationProvider // Enabled if provider exists
			},
			protectionPlans: {
				...currentSteps.protectionPlans,
				enabled: !!drConfig.products?.hasFi // Enabled if F&I products available
			},
			creditStatus: {
				...currentSteps.creditStatus,
				enabled: !!drConfig.showCreditAppButton // Enabled if credit app allowed
			},
			testDrive: {
				...currentSteps.testDrive,
				enabled: !!drConfig.products?.hasTestDrive // Enabled if test drive available
			},
			dealXgId: currentSteps.dealXgId
		},
		vehicleId
	};
};

const Widget = () => {
	const { trackMyOfferView } = usePrefs();
	const dispatch = useDispatch();
	const steps = useSelector((state) => state.steps);
	const isPaymentSelectorView = trackMyOfferView === 'paymentSelector';
	const flags = useFlags();
	const isFetchDRAdminConfig =
		flags['ws-track-my-offer-use-dr-admin-cta-config'] || false;

	useEffect(() => {
		const initializeWidget = async () => {
			// Handle DR Admin Config first if enabled
			if (isFetchDRAdminConfig) {
				try {
					const result = await fetchAndUpdateDRConfig(steps);

					if (result) {
						const { updatedSteps, vehicleId } = result;
						dispatch(setSteps(updatedSteps));
						updateLsWidgetState(vehicleId, updatedSteps);
					}
				} catch (error) {
					logger.error('Failed to fetch DR admin config:', error);
					// Fallback: use default enabled states from Redux
				}
			}
		};
		// TODO: refactor to set these on server side
		const uuid = window?.DDC?.dataLayer?.vehicles[0]?.uuid || '';
		const listingType =
			window?.DDC?.dataLayer?.vehicles[0]?.inventoryType || '';

		// payment selector view handles read, write from local storage and publish steps to step tracker
		if (uuid && isPaymentSelectorView) {
			const lsSteps = getLsWidgetStateByVehicleId(uuid, steps);
			// TODO: validate LS steps before pushing to state
			dispatch(setSteps(lsSteps));

			// TODO: refactor to set these on server side
			dispatch(setVehicleId(uuid));
			dispatch(setListingType(listingType));
		}

		initializeWidget();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return isPaymentSelectorView ? <PaymentSelectorView /> : <StepContainer />;
};

Widget.propTypes = {};

export default Widget;
